import { safePathEncode } from '@/utils/safePath'
import { apiSlice } from './apiSlice'

export interface Consignment {
  id?: number | null
  contractNo: string
  contractDate: string | null
  clientCode: string
  clientName?: string | null
  quantityKg: number | null
  packagingCode?: string | null
  packaging?: string | null
  qualityCode: string
  certification: string
  lastFixingDate: string | null
  dateUpdate?: string | null
  personUpdate?: string | null
  createdDate?: string | null
  createdBy?: string | null
  updatedDate?: string | null
  updatedBy?: string | null
  isStatus?: number | null
}

export type ConsignmentPayload = Pick<
  Consignment,
  | 'contractNo'
  | 'contractDate'
  | 'clientCode'
  | 'quantityKg'
  | 'packagingCode'
  | 'packaging'
  | 'qualityCode'
  | 'certification'
  | 'lastFixingDate'
>

export interface ConsignmentListResponse {
  items: Consignment[]
  total: number
  page: number
  pageSize: number
}

export interface ConsignmentListQuery {
  page: number
  pageSize: number
  q?: string
  clientCode?: string
  certification?: string
}

const consignmentApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getConsignments: builder.query<ConsignmentListResponse, ConsignmentListQuery>({
      query: ({ page, pageSize, q, clientCode, certification }) => ({
        url: '/consignments',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(clientCode ? { clientCode } : {}),
          ...(certification ? { certification } : {}),
        },
      }),
      providesTags: ['SaleContract'],
    }),
    getConsignment: builder.query<Consignment, string>({
      query: (contractNo) => `/consignments/${safePathEncode(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [{ type: 'SaleContract', id: `consignment-${contractNo}` }],
    }),
    createConsignment: builder.mutation<Consignment, ConsignmentPayload>({
      query: (body) => ({ url: '/consignments', method: 'POST', body }),
      invalidatesTags: ['SaleContract'],
    }),
    updateConsignment: builder.mutation<Consignment, { contractNo: string; body: ConsignmentPayload }>({
      query: ({ contractNo, body }) => ({
        url: `/consignments/${safePathEncode(contractNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SaleContract'],
    }),
    cancelConsignment: builder.mutation<Consignment, string>({
      query: (contractNo) => ({
        url: `/consignments/${safePathEncode(contractNo)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['SaleContract'],
    }),
  }),
})

export const {
  useGetConsignmentsQuery,
  useGetConsignmentQuery,
  useCreateConsignmentMutation,
  useUpdateConsignmentMutation,
  useCancelConsignmentMutation,
} = consignmentApi

export default consignmentApi
